import { Fragment, useCallback, useState } from 'react';
import '../styles/hero/hero.css';
import { captureEvent } from '../lib/analytics';
import { goToSection } from '../lib/smoothScroll';
import FlowBackground from './parts/FlowBackground';
import HeroStage from './parts/HeroStage';
import { DEFAULT_LOOK, LOOKS, type Look } from '../data/stageLooks';

interface HeroProps {
    /** False while the loader covers the page; the headline enters once it is true. */
    ready: boolean;
}

interface Word {
    text: string;
    /** Section the word links to. Words with a target are the hero's keywords. */
    to?: string;
}

/* One statement. The keywords are real links: hover dims the rest of the sentence to 20%. */
const HEADLINE: Word[] = [
    { text: 'Ik' },
    { text: 'bouw' },
    { text: 'websites,', to: 'projects' },
    { text: 'webapps', to: 'services' },
    { text: 'en' },
    { text: 'maatwerk software', to: 'services' },
    { text: 'waarmee' },
    { text: 'ondernemers' },
    { text: 'online' },
    { text: 'sterker' },
    { text: 'staan.' },
];

const Hero = ({ ready }: HeroProps) => {
    // The project on the device stage sets the mood: the background flows in its colours and the whole
    // hero turns dark or light with it (text, buttons and the dock follow through the theme tokens).
    const [look, setLook] = useState<Look>(DEFAULT_LOOK);
    const handleActive = useCallback((id: string) => setLook(LOOKS[id] ?? DEFAULT_LOOK), []);

    return (
        <section id="hero" className="hero" data-theme={look.mode} data-ready={ready} aria-label="Introductie">
            <div className="hero__media" aria-hidden="true">
                <FlowBackground look={look} />
                <div className="hero__shade" />
            </div>

            <HeroStage play={ready} onActive={handleActive} />

            <div className="container hero__top">
                <span className="label brand">Schmitt Software</span>
                <span className="label">Geel, België</span>
            </div>

            <div className="container hero__body">
                <span className="hero__bar" aria-hidden="true" />
                <h1 id="hero-title" className="hero__title display">
                    {HEADLINE.map((word, index) => (
                        <Fragment key={word.text}>
                            <span className="hero__mask">
                                <span className="hero__word" style={{ '--i': index } as React.CSSProperties}>
                                    {word.to ? (
                                        <a
                                            className="kw"
                                            href={`#${word.to}`}
                                            onClick={(event) => {
                                                event.preventDefault();
                                                captureEvent('hero_keyword_clicked', { target: word.to });
                                                goToSection(word.to!);
                                            }}
                                        >
                                            {word.text}
                                        </a>
                                    ) : (
                                        word.text
                                    )}
                                </span>
                            </span>{' '}
                        </Fragment>
                    ))}
                </h1>

                <div className="hero__foot">
                    <p className="hero__tagline">Simple, reliable software.</p>
                    <div className="hero__cta">
                        <a
                            className="pill pill--solid"
                            href="#contact"
                            onClick={(event) => {
                                event.preventDefault();
                                captureEvent('hero_cta_clicked', { target: 'contact' });
                                goToSection('contact');
                            }}
                        >
                            Vraag een offerte aan
                        </a>
                        <a
                            className="pill"
                            href="#services"
                            onClick={(event) => {
                                event.preventDefault();
                                captureEvent('hero_cta_clicked', { target: 'services' });
                                goToSection('services');
                            }}
                        >
                            Bekijk diensten
                        </a>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Hero;
